import { Plan } from '@core/types';
import type { AccountType } from '../features/auth/types';
import type { BigEventScreen } from '../features/events/types/access';

/**
 * Menu item configuration
 */
export interface MenuItem {
  /** Display label */
  label: string;
  /** Icon (emoji or component) */
  icon?: string;
  /** Route path (if direct link) */
  path?: string;
  /** Child menu items (for nested menus) */
  children?: MenuItem[];
  /** External URL (opens in new tab) */
  externalUrl?: string;
  /** Minimum roles required to see this item */
  roles?: AccountType[];
  /** Attention count shown as a pill (hidden when 0/absent); groups carry their children's total. */
  badge?: number;
}

/**
 * Menu item definitions with icons
 */
const MENU_ITEMS = {
  // Core items (all users)
  HOME: { label: 'Home', icon: '🏠', path: '/home' } as MenuItem,
  INSIGHT_CENTER: { label: 'Insight Center', icon: '💡', path: '/insight-center' } as MenuItem,
  ONBOARDING_GAME: { label: 'Onboarding Game', icon: '🎮', path: '/onboarding-game' } as MenuItem,
  // The Training Center group's first child (TRAINING_CENTER_GROUP).
  PROMOTION: { label: 'Promotions', icon: '🏆', path: '/promotion/dashboard' } as MenuItem,
  
  // Tools
  SYSTEMATIC_TOOLS: { label: '10 Systematic Tools', icon: '🛠️', path: '/systematic-tools' } as MenuItem,
  // The Training Center group's second child, after Promotions; the group itself is TRAINING_CENTER_GROUP.
  TRAINING_CENTER: { label: 'Training', icon: '🎓', path: '/training-center' } as MenuItem,
  TRAINING_SCHEDULE: { label: 'Training Schedule', icon: '📅', path: '/training-schedule' } as MenuItem,
  CALENDAR: { label: 'Calendar', icon: '📆', path: '/calendar' } as MenuItem,
  FILE_VAULT: { label: 'File Vault', icon: '📁', path: '/file-vault' } as MenuItem,
  HELP_DESK: { label: 'Help Desk', icon: '❓', path: '/helpdesk' } as MenuItem,
  MATCHUP: { label: 'Matchup', icon: '🤝', path: '/matchup' } as MenuItem,

  // BPM (Business Presentation Meetings)
  BPM_OVERVIEW: { label: 'BPM Overview', icon: '📊', path: '/bpm/overview' } as MenuItem,
  BPM_ADD_GUEST: { label: 'Add Guest', icon: '👤', path: '/bpm/add-guest' } as MenuItem,
  BPM_VIEW_INVITES: { label: 'Guest Invites', icon: '📬', path: '/bpm/view-invites' } as MenuItem,
  BPM_ASSOCIATE_INVITES: { label: 'Associate Invites', icon: '📇', path: '/bpm/associate-invites' } as MenuItem,
  BPM_GUEST_CHECKIN: { label: 'Guest Check-In', icon: '✔️', path: '/bpm/guest-checkin' } as MenuItem,
  BPM_ASSOCIATE_CHECKIN: { label: 'Associate Check-In', icon: '✅', path: '/bpm/associate-checkin' } as MenuItem,
  BPM_SCHEDULE: { label: 'BPM Schedule', icon: '📅', path: '/bpm/schedule' } as MenuItem,
  // Last sub-link. Gated per-user by bpm_settings:manage: getMenuForUser drops it
  // unless the caller reports `canManageBpmSettings`, and the page re-checks.
  BPM_SETTINGS: { label: 'BPM Settings', icon: '⚙️', path: '/bpm/settings' } as MenuItem,


  // Licensing items
  TRACK_LICENSE: { label: 'Track My License', icon: '📋', path: '/licensing/track' } as MenuItem,
  LICENSING_DOCS: { label: 'Licensing Documents', icon: '📄', path: '/licensing/documents' } as MenuItem,
  CRASH_COURSE: { label: 'Crash Course', icon: '🚀', path: '/licensing/crash-course' } as MenuItem,
  
  // My Team items
  ORG_CHART: { label: 'Org Chart', icon: '📊', path: '/team/org-chart' } as MenuItem,
  PROSPECT_TRACKER: { label: 'Prospect Tracker', icon: '👥', path: '/team/prospect-tracker' } as MenuItem,
  MISSION_TRACKER: { label: 'Mission Tracker', icon: '📈', path: '/team/mission-tracker' } as MenuItem,
  ASSOCIATE_TRACKER: { label: 'Associate Tracker', icon: '👔', path: '/team/associate-tracker' } as MenuItem,
  // Listed for every plan. The page itself decides between the full page, the
  // member view and the locked teaser (team-program-gate.tsx).
  PRODUCERS: { label: 'Producers', icon: '💼', path: '/team/producers' } as MenuItem,
  BUILDERS: { label: 'Builders', icon: '🏗️', path: '/team/builders' } as MenuItem,

  // Builder AI
  BUILDER_AI_HOME: { label: 'Home', icon: '🏠', path: '/builder-ai/home' } as MenuItem,
  BUILDER_AI_COMPANY: { label: 'Company', icon: '🏢', path: '/builder-ai/company' } as MenuItem,
  BUILDER_AI_BASESHOP: { label: 'Baseshop', icon: '🏬', path: '/builder-ai/baseshop' } as MenuItem,
  BUILDER_AI_INVITATIONS: { label: 'Invitations', icon: '✉️', path: '/builder-ai/invitations' } as MenuItem,
  BUILDER_AI_REPORTING: { label: 'Reporting', icon: '📈', path: '/builder-ai/reporting' } as MenuItem,
  BUILDER_AI_BULLETIN: { label: 'Bulletin', icon: '🏆', path: '/builder-ai/bulletin' } as MenuItem,
  // Data Integrity (admin-only diagnostic reports; gated per-user by misalignments:read)
  LEADER_MISALIGNMENTS: {
    label: 'Leader Misalignments',
    icon: '⚠️',
    path: '/admin/data-integrity/leader-misalignments',
  } as MenuItem,
  POLICY_MISALIGNMENTS: {
    label: 'Policy Misalignments',
    icon: '📋',
    path: '/admin/data-integrity/policy-misalignments',
  } as MenuItem,
  // Product catalog management (gated per-user by products:read)
  PRODUCTS: { label: 'Products', icon: '📦', path: '/admin/products' } as MenuItem,
  // Prime Corporate Services booking. The member page is for every signed-in user (the
  // backend tells an uncoded account why it cannot book); the activity trail is gated
  // per-user by prime_booking:read.
  PRIME_BOOKING_ACTIVITY: {
    label: 'Prime Booking Activity',
    icon: '🧾',
    path: '/admin/prime-booking-activity',
  } as MenuItem,
  // XCEL prelicensing intake and mapping review, gated per-user by
  // xcel_prelicensing:view|review|manage.
  XCEL_PRELICENSING: {
    label: 'XCEL Prelicensing',
    icon: '🎓',
    path: '/admin/xcel-prelicensing',
  } as MenuItem,
  // Contest configuration, gated per-user by wbreporting:manage (not homev2:read).
  CONTEST_SETTINGS: {
    label: 'Contest Settings',
    icon: '🎛️',
    path: '/admin/contest-settings',
  } as MenuItem,
  // Code of Honor committee review and admin rules/values, gated per-user by the
  // code_of_honor:* access-console permissions. Members use the card on Home.
  CODE_OF_HONOR: { label: 'Code of Honor', icon: '🏅', path: '/code-of-honor/committee' } as MenuItem,
  // Guidance library and review queue, gated per-user by gms:author (no role holds it).
  GUIDANCE: { label: 'Guidance', icon: '🧭', path: '/admin/guidance' } as MenuItem,
  // Plug-in fees admin overview (the first child of the "Plug-in Fees" group),
  // gated per-user by plugin_fees:manage or :review.
  PLUGIN_FEES_OVERVIEW: {
    label: 'Overview',
    icon: '📊',
    path: '/admin/plugin-fees',
  } as MenuItem,
  // Plug-in fee office/assistant review queues, gated per-user by plugin_fees:review
  // (the Hierarchy Assistant). No plan grants it.
  PLUGIN_FEES_REVIEW: {
    label: 'Reviews',
    icon: '🗂️',
    path: '/admin/plugin-fees/review',
  } as MenuItem,
  // Plug-in fee billing cycles (preview, reports, approve), gated per-user by
  // plugin_fees:manage, :review or :payout_approve.
  PLUGIN_FEES_CYCLES: {
    label: 'Billing Cycles',
    icon: '🧾',
    path: '/admin/plugin-fees/cycles',
  } as MenuItem,
  // Plug-in fee collection: payments dashboard and follow-ups, gated per-user by
  // plugin_fees:manage or :review.
  PLUGIN_FEES_PAYMENTS: {
    label: 'Fee Payments',
    icon: '💵',
    path: '/admin/plugin-fees/payments',
  } as MenuItem,
  // Quarterly SMD payouts (Stripe Connect), gated per-user by plugin_fees:manage, :review
  // or :payout_approve.
  PLUGIN_FEES_PAYOUTS: {
    label: 'Payouts',
    icon: '🏦',
    path: '/admin/plugin-fees/payouts',
  } as MenuItem,
  // SEVC totals by month, gated per-user by plugin_fees:manage or :review.
  PLUGIN_FEES_SEVC_TOTALS: {
    label: 'SEVC Totals',
    icon: '📈',
    path: '/admin/plugin-fees/sevc-totals',
  } as MenuItem,
  // Manual SMD ledger adjustments, gated per-user by plugin_fees:manage.
  PLUGIN_FEES_ADJUSTMENTS: {
    label: 'Adjustments',
    icon: '⚖️',
    path: '/admin/plugin-fees/adjustments',
  } as MenuItem,
  // Recognition and mailing costs charged to SMDs, gated per-user by plugin_fees:manage.
  PLUGIN_FEES_COSTS: {
    label: 'Recognition Orders',
    icon: '🎖️',
    path: '/admin/plugin-fees/costs',
  } as MenuItem,
  // Plug-in fee schedule, billing settings and their change history, gated per-user by
  // plugin_fees:manage, :review or :payout_approve (editing: :manage, inside the page).
  PLUGIN_FEES_SETTINGS: {
    label: 'Fee Settings',
    icon: '🛠️',
    path: '/admin/plugin-fees/settings',
  } as MenuItem,
  // WB reporting pipeline operations (gated per-user by wbreporting:read/manage)
  REPORTING_PIPELINE: {
    label: 'Reporting Pipeline',
    icon: '⚙️',
    path: '/admin/reporting-pipeline',
  } as MenuItem,
  LICENSING_TRACKER: { label: 'Licensing Tracker', icon: '📝', path: '/team/licensing-tracker' } as MenuItem,
  PRODUCTION_TRACKER: { label: 'Production Tracker', icon: '💰', path: '/team/production-tracker' } as MenuItem,
  TEAM_PROMOTION: { label: 'Team Promotion Tracker', icon: '📈', path: '/promotion/team' } as MenuItem,
  INVITE_AGENTS: { label: 'Invite Agents', icon: '📨', path: '/admin/invite-agents' } as MenuItem,
  TERMINATED_USERS: { label: 'Terminated Users', icon: '🚫', path: '/terminated-users' } as MenuItem,
  
  // Big Event items
  BIG_EVENT_BUILDER: { label: 'Big Event Builder', icon: '🎪', path: '/events' } as MenuItem,
  PURCHASES: { label: 'Purchases', icon: '🛒', path: '/events/purchases' } as MenuItem,
  CHECK_IN: { label: 'Check-in', icon: '✅', path: '/events/check-in' } as MenuItem,
  PERMISSIONS: { label: 'Permissions', icon: '🔐', path: '/events/permissions' } as MenuItem,
  RECOGNITION_ORDERS: { label: 'Recognition Orders', icon: '🏆', path: '/events/recognition' } as MenuItem,
  EXTERNAL_TEAMS: { label: 'External Teams', icon: '🤝', path: '/events/external-teams' } as MenuItem,
  CONVENTION_TRACKING: { label: 'Convention Tracking', icon: '🎟️', path: '/events/convention-tracking' } as MenuItem,
  
  // Admin items
  MISSION_RING_PROOF_ADMIN: { label: 'Mission Ring Proof', icon: '💍', path: '/admin/mission-ring-proof' } as MenuItem,
  HELPDESK_ADMIN: { label: 'Support Queue', icon: '🆘', path: '/admin/helpdesk' } as MenuItem,
  // Helpdesk L1 queue for an active SMD (and their assistant): my-access `team_queue`.
  TEAM_SUPPORT: { label: 'Team Support', icon: '🎧', path: '/team/support' } as MenuItem,
  // Feature requests: feature_requests:create (asking) / :manage (developer board).
  FEATURE_REQUESTS: { label: 'Feature Requests', icon: '💡', path: '/feature-requests' } as MenuItem,
  FEATURE_BOARD: { label: 'Feature Board', icon: '🛠️', path: '/admin/feature-requests' } as MenuItem,
  FUNCTIONS: { label: 'Functions', icon: '🧩', path: '/admin/functions' } as MenuItem,
  USER_PERMISSIONS: { label: 'User Permissions', icon: '🔐', path: '/admin/user-permissions' } as MenuItem,
  LEVEL_PERMISSIONS: { label: 'Level Permissions', icon: '📊', path: '/admin/level-permissions' } as MenuItem,
  FILE_VAULT_ADMIN: { label: 'File Vault', icon: '📁', path: '/admin/file-vault' } as MenuItem,
  TRAINING_CENTER_ADMIN: { label: 'Training Center', icon: '🎓', path: '/admin/training-center' } as MenuItem,
  SYSTEMATIC_TOOLS_ADMIN: { label: '10 Systematic Tools', icon: '🛠️', path: '/admin/systematic-tools' } as MenuItem,
  HOME_CONTENT_ADMIN: { label: 'Home Content', icon: '🎬', path: '/admin/home-content' } as MenuItem,
  PROMOTION_ADMIN: { label: 'Promotion Management', icon: '🎞️', path: '/admin/promotion' } as MenuItem,
};

/**
 * Builder AI group — visibility is not plan-based. It is injected by
 * `getMenuForUser` as the last child of My Team for any user the backend
 * `/api/builderai/my-access/` endpoint reports `can_view: true` for (owners,
 * active builders, and pending invitees).
 */
const BUILDER_AI_GROUP: MenuItem = {
  label: 'Builder AI',
  icon: '🌱',
  children: [
    MENU_ITEMS.BUILDER_AI_HOME,
    MENU_ITEMS.BUILDER_AI_COMPANY,
    MENU_ITEMS.BUILDER_AI_BASESHOP,
    MENU_ITEMS.BUILDER_AI_INVITATIONS,
    MENU_ITEMS.BUILDER_AI_REPORTING,
    MENU_ITEMS.BUILDER_AI_BULLETIN,
  ],
};

/**
 * Data Integrity group — admin-only diagnostic reports. Like Builder AI, this
 * is NOT plan-based: it is injected by `getMenuForUser` only when the backend
 * `/api/misalignments/my-access/` endpoint reports `can_view: true` (i.e. the
 * user was granted `misalignments:read` in the access console).
 */
const DATA_INTEGRITY_GROUP: MenuItem = {
  label: 'Data Integrity',
  icon: '🩺',
  children: [MENU_ITEMS.LEADER_MISALIGNMENTS, MENU_ITEMS.POLICY_MISALIGNMENTS],
};

/**
 * Plug-in fees admin group — like Data Integrity, not plan-based. `getMenuForUser`
 * builds its children from the per-user plug-in fee grants and adds the group, nested
 * in Admin, only when at least one child is visible.
 */
const PLUGIN_FEES_GROUP_LABEL = 'White Glove Concierge';

/** Every plan has a My Team group; Builder AI nests in it. */
const MY_TEAM_LABEL = 'My Team';

/** The Admin and Super Admin plans' Admin group; Products, Reporting Pipeline, Contest Settings, Code of Honor and Plug-in Fees nest in it. */
const ADMIN_GROUP_LABEL = 'Admin';

/**
 * Big Event group — not plan-based. Each child is added when `/api/events/events/my-access/`
 * reports its screen open for at least one event, through a platform-wide grant (role,
 * level or a User Permissions override) or a per-event grant (Big Event → Permissions).
 * A check-in-only delegate gets the group with just Check-in.
 */
const BIG_EVENT_GROUP_LABEL = 'Big Event';
const BIG_EVENT_ENTRIES: Array<[BigEventScreen, MenuItem]> = [
  ['builder', MENU_ITEMS.BIG_EVENT_BUILDER],
  ['purchases', MENU_ITEMS.PURCHASES],
  ['checkin', MENU_ITEMS.CHECK_IN],
  ['permissions', MENU_ITEMS.PERMISSIONS],
  ['recognition', MENU_ITEMS.RECOGNITION_ORDERS],
  ['external_teams', MENU_ITEMS.EXTERNAL_TEAMS],
  ['convention_tracking', MENU_ITEMS.CONVENTION_TRACKING],
];

/**
 * Training Center group — every plan lists it straight after My Team. Promotions nests
 * here, listed first (PR11); for a New Agent without `hasPromotionAccess`, `getMenuForUser` removes that child
 * and the group keeps Training.
 */
const TRAINING_CENTER_GROUP: MenuItem = {
  label: 'Training Center',
  icon: '🎓',
  children: [MENU_ITEMS.PROMOTION, MENU_ITEMS.TRAINING_CENTER],
};

/**
 * Plan-based menu structures
 * These mirror the old site's getSidebarStructure() function
 */
export const PLAN_MENUS = {
  [Plan.NewAgent]: [
    MENU_ITEMS.HOME,
    MENU_ITEMS.INSIGHT_CENTER,
    MENU_ITEMS.ONBOARDING_GAME,
    {
      label: 'Licensing',
      icon: '📜',
      children: [
        MENU_ITEMS.TRACK_LICENSE,
        MENU_ITEMS.CRASH_COURSE,
      ],
    },
    MENU_ITEMS.SYSTEMATIC_TOOLS,
    {
      label: 'My Team',
      icon: '👥',
      children: [
        MENU_ITEMS.PROSPECT_TRACKER,
        MENU_ITEMS.PRODUCERS,
        MENU_ITEMS.BUILDERS,
      ],
    },
    TRAINING_CENTER_GROUP,
    MENU_ITEMS.MATCHUP,
    {
      label: 'BPM',
      icon: '📊',
      children: [
        MENU_ITEMS.BPM_OVERVIEW,
        MENU_ITEMS.BPM_ADD_GUEST,
        MENU_ITEMS.BPM_VIEW_INVITES,
        MENU_ITEMS.BPM_ASSOCIATE_INVITES,
        MENU_ITEMS.BPM_GUEST_CHECKIN,
        MENU_ITEMS.BPM_ASSOCIATE_CHECKIN,
        MENU_ITEMS.BPM_SCHEDULE,
        MENU_ITEMS.BPM_SETTINGS,
      ],
    },
    MENU_ITEMS.TRAINING_SCHEDULE,
    MENU_ITEMS.FILE_VAULT,
    MENU_ITEMS.HELP_DESK,
  ] as MenuItem[],
  
  [Plan.Agent]: [
    MENU_ITEMS.HOME,
    MENU_ITEMS.INSIGHT_CENTER,
    MENU_ITEMS.ONBOARDING_GAME,
    {
      label: 'Licensing',
      icon: '📜',
      children: [
        MENU_ITEMS.TRACK_LICENSE,
        MENU_ITEMS.LICENSING_DOCS,
        MENU_ITEMS.CRASH_COURSE,
      ],
    },
    MENU_ITEMS.SYSTEMATIC_TOOLS,
    {
      label: 'My Team',
      icon: '👥',
      children: [
        MENU_ITEMS.ORG_CHART,
        MENU_ITEMS.PROSPECT_TRACKER,
        MENU_ITEMS.PRODUCTION_TRACKER,
        MENU_ITEMS.PRODUCERS,
        MENU_ITEMS.BUILDERS,
      ],
    },
    TRAINING_CENTER_GROUP,
    MENU_ITEMS.MATCHUP,
    {
      label: 'BPM',
      icon: '📊',
      children: [
        MENU_ITEMS.BPM_OVERVIEW,
        MENU_ITEMS.BPM_ADD_GUEST,
        MENU_ITEMS.BPM_VIEW_INVITES,
        MENU_ITEMS.BPM_ASSOCIATE_INVITES,
        MENU_ITEMS.BPM_GUEST_CHECKIN,
        MENU_ITEMS.BPM_ASSOCIATE_CHECKIN,
        MENU_ITEMS.BPM_SCHEDULE,
        MENU_ITEMS.BPM_SETTINGS,
      ],
    },
    MENU_ITEMS.TRAINING_SCHEDULE,
    MENU_ITEMS.CALENDAR,
    MENU_ITEMS.FILE_VAULT,
    MENU_ITEMS.HELP_DESK,
  ] as MenuItem[],
  
  [Plan.Leader]: [
    MENU_ITEMS.HOME,
    MENU_ITEMS.INSIGHT_CENTER,
    MENU_ITEMS.ONBOARDING_GAME,
    {
      label: 'Licensing',
      icon: '📜',
      children: [
        MENU_ITEMS.TRACK_LICENSE,
        MENU_ITEMS.LICENSING_DOCS,
        MENU_ITEMS.CRASH_COURSE,
      ],
    },
    MENU_ITEMS.SYSTEMATIC_TOOLS,
    {
      label: 'My Team',
      icon: '👥',
      children: [
        MENU_ITEMS.ORG_CHART,
        MENU_ITEMS.PROSPECT_TRACKER,
        MENU_ITEMS.MISSION_TRACKER,
        MENU_ITEMS.ASSOCIATE_TRACKER,
        MENU_ITEMS.LICENSING_TRACKER,
        MENU_ITEMS.PRODUCTION_TRACKER,
        MENU_ITEMS.TEAM_PROMOTION,
        MENU_ITEMS.PRODUCERS,
        MENU_ITEMS.BUILDERS,
      ],
    },
    TRAINING_CENTER_GROUP,
    MENU_ITEMS.MATCHUP,
    {
      label: 'BPM',
      icon: '📊',
      children: [
        MENU_ITEMS.BPM_OVERVIEW,
        MENU_ITEMS.BPM_ADD_GUEST,
        MENU_ITEMS.BPM_VIEW_INVITES,
        MENU_ITEMS.BPM_ASSOCIATE_INVITES,
        MENU_ITEMS.BPM_GUEST_CHECKIN,
        MENU_ITEMS.BPM_ASSOCIATE_CHECKIN,
        MENU_ITEMS.BPM_SCHEDULE,
        MENU_ITEMS.BPM_SETTINGS,
      ],
    },
    MENU_ITEMS.TRAINING_SCHEDULE,
    MENU_ITEMS.CALENDAR,
    MENU_ITEMS.FILE_VAULT,
    MENU_ITEMS.HELP_DESK,
    MENU_ITEMS.INVITE_AGENTS,
  ] as MenuItem[],
  
  [Plan.Broker]: [
    MENU_ITEMS.HOME,
    MENU_ITEMS.INSIGHT_CENTER,
    MENU_ITEMS.ONBOARDING_GAME,
    {
      label: 'Licensing',
      icon: '📜',
      children: [
        MENU_ITEMS.TRACK_LICENSE,
        MENU_ITEMS.LICENSING_DOCS,
        MENU_ITEMS.CRASH_COURSE,
      ],
    },
    MENU_ITEMS.SYSTEMATIC_TOOLS,
    {
      label: 'My Team',
      icon: '👥',
      children: [
        MENU_ITEMS.ORG_CHART,
        MENU_ITEMS.PROSPECT_TRACKER,
        MENU_ITEMS.MISSION_TRACKER,
        MENU_ITEMS.ASSOCIATE_TRACKER,
        MENU_ITEMS.LICENSING_TRACKER,
        MENU_ITEMS.PRODUCTION_TRACKER,
        MENU_ITEMS.TEAM_PROMOTION,
        MENU_ITEMS.PRODUCERS,
        MENU_ITEMS.BUILDERS,
      ],
    },
    TRAINING_CENTER_GROUP,
    MENU_ITEMS.MATCHUP,
    {
      label: 'BPM',
      icon: '📊',
      children: [
        MENU_ITEMS.BPM_OVERVIEW,
        MENU_ITEMS.BPM_ADD_GUEST,
        MENU_ITEMS.BPM_VIEW_INVITES,
        MENU_ITEMS.BPM_ASSOCIATE_INVITES,
        MENU_ITEMS.BPM_GUEST_CHECKIN,
        MENU_ITEMS.BPM_ASSOCIATE_CHECKIN,
        MENU_ITEMS.BPM_SCHEDULE,
        MENU_ITEMS.BPM_SETTINGS,
      ],
    },
    MENU_ITEMS.TRAINING_SCHEDULE,
    MENU_ITEMS.CALENDAR,
    MENU_ITEMS.FILE_VAULT,
    MENU_ITEMS.HELP_DESK,
    MENU_ITEMS.INVITE_AGENTS,
  ] as MenuItem[],
  
  // Senior Broker uses same menu as Broker
  [Plan.SeniorBroker]: [
    MENU_ITEMS.HOME,
    MENU_ITEMS.INSIGHT_CENTER,
    MENU_ITEMS.ONBOARDING_GAME,
    {
      label: 'Licensing',
      icon: '📜',
      children: [
        MENU_ITEMS.TRACK_LICENSE,
        MENU_ITEMS.LICENSING_DOCS,
        MENU_ITEMS.CRASH_COURSE,
      ],
    },
    MENU_ITEMS.SYSTEMATIC_TOOLS,
    {
      label: 'My Team',
      icon: '👥',
      children: [
        MENU_ITEMS.ORG_CHART,
        MENU_ITEMS.PROSPECT_TRACKER,
        MENU_ITEMS.MISSION_TRACKER,
        MENU_ITEMS.ASSOCIATE_TRACKER,
        MENU_ITEMS.LICENSING_TRACKER,
        MENU_ITEMS.PRODUCTION_TRACKER,
        MENU_ITEMS.TEAM_PROMOTION,
        MENU_ITEMS.PRODUCERS,
        MENU_ITEMS.BUILDERS,
      ],
    },
    TRAINING_CENTER_GROUP,
    MENU_ITEMS.MATCHUP,
    {
      label: 'BPM',
      icon: '📊',
      children: [
        MENU_ITEMS.BPM_OVERVIEW,
        MENU_ITEMS.BPM_ADD_GUEST,
        MENU_ITEMS.BPM_VIEW_INVITES,
        MENU_ITEMS.BPM_ASSOCIATE_INVITES,
        MENU_ITEMS.BPM_GUEST_CHECKIN,
        MENU_ITEMS.BPM_ASSOCIATE_CHECKIN,
        MENU_ITEMS.BPM_SCHEDULE,
        MENU_ITEMS.BPM_SETTINGS,
      ],
    },
    MENU_ITEMS.TRAINING_SCHEDULE,
    MENU_ITEMS.CALENDAR,
    MENU_ITEMS.FILE_VAULT,
    MENU_ITEMS.HELP_DESK,
    MENU_ITEMS.INVITE_AGENTS,
  ] as MenuItem[],
  
  [Plan.Admin]: [
    MENU_ITEMS.HOME,
    MENU_ITEMS.INSIGHT_CENTER,
    MENU_ITEMS.ONBOARDING_GAME,
    {
      label: 'Licensing',
      icon: '📜',
      children: [
        MENU_ITEMS.TRACK_LICENSE,
        MENU_ITEMS.LICENSING_DOCS,
        MENU_ITEMS.CRASH_COURSE,
      ],
    },
    MENU_ITEMS.SYSTEMATIC_TOOLS,
    {
      label: 'My Team',
      icon: '👥',
      children: [
        MENU_ITEMS.ORG_CHART,
        MENU_ITEMS.PROSPECT_TRACKER,
        MENU_ITEMS.MISSION_TRACKER,
        MENU_ITEMS.ASSOCIATE_TRACKER,
        MENU_ITEMS.LICENSING_TRACKER,
        MENU_ITEMS.PRODUCTION_TRACKER,
        MENU_ITEMS.TEAM_PROMOTION,
        MENU_ITEMS.PRODUCERS,
        MENU_ITEMS.BUILDERS,
      ],
    },
    TRAINING_CENTER_GROUP,
    MENU_ITEMS.MATCHUP,
    {
      label: 'BPM',
      icon: '📊',
      children: [
        MENU_ITEMS.BPM_OVERVIEW,
        MENU_ITEMS.BPM_ADD_GUEST,
        MENU_ITEMS.BPM_VIEW_INVITES,
        MENU_ITEMS.BPM_ASSOCIATE_INVITES,
        MENU_ITEMS.BPM_GUEST_CHECKIN,
        MENU_ITEMS.BPM_ASSOCIATE_CHECKIN,
        MENU_ITEMS.BPM_SCHEDULE,
        MENU_ITEMS.BPM_SETTINGS,
      ],
    },
    MENU_ITEMS.TRAINING_SCHEDULE,
    MENU_ITEMS.CALENDAR,
    MENU_ITEMS.FILE_VAULT,
    MENU_ITEMS.HELP_DESK,
    MENU_ITEMS.INVITE_AGENTS,
    MENU_ITEMS.TERMINATED_USERS,
    {
      label: 'Admin',
      icon: '👨‍💼',
      children: [
        MENU_ITEMS.MISSION_RING_PROOF_ADMIN,
        MENU_ITEMS.HELPDESK_ADMIN,
        MENU_ITEMS.FUNCTIONS,
        MENU_ITEMS.FILE_VAULT_ADMIN,
        MENU_ITEMS.TRAINING_CENTER_ADMIN,
        MENU_ITEMS.SYSTEMATIC_TOOLS_ADMIN,
        MENU_ITEMS.HOME_CONTENT_ADMIN,
        MENU_ITEMS.PROMOTION_ADMIN,
        MENU_ITEMS.USER_PERMISSIONS,
        MENU_ITEMS.LEVEL_PERMISSIONS,
      ],
    },
  ] as MenuItem[],

  [Plan.SuperAdmin]: [
    MENU_ITEMS.HOME,
    MENU_ITEMS.INSIGHT_CENTER,
    MENU_ITEMS.ONBOARDING_GAME,
    {
      label: 'Licensing',
      icon: '📜',
      children: [
        MENU_ITEMS.TRACK_LICENSE,
        MENU_ITEMS.LICENSING_DOCS,
        MENU_ITEMS.CRASH_COURSE,
      ],
    },
    MENU_ITEMS.SYSTEMATIC_TOOLS,
    {
      label: 'My Team',
      icon: '👥',
      children: [
        MENU_ITEMS.ORG_CHART,
        MENU_ITEMS.PROSPECT_TRACKER,
        MENU_ITEMS.MISSION_TRACKER,
        MENU_ITEMS.ASSOCIATE_TRACKER,
        MENU_ITEMS.LICENSING_TRACKER,
        MENU_ITEMS.PRODUCTION_TRACKER,
        MENU_ITEMS.TEAM_PROMOTION,
        MENU_ITEMS.PRODUCERS,
        MENU_ITEMS.BUILDERS,
      ],
    },
    TRAINING_CENTER_GROUP,
    MENU_ITEMS.MATCHUP,
    {
      label: 'BPM',
      icon: '📊',
      children: [
        MENU_ITEMS.BPM_OVERVIEW,
        MENU_ITEMS.BPM_ADD_GUEST,
        MENU_ITEMS.BPM_VIEW_INVITES,
        MENU_ITEMS.BPM_ASSOCIATE_INVITES,
        MENU_ITEMS.BPM_GUEST_CHECKIN,
        MENU_ITEMS.BPM_ASSOCIATE_CHECKIN,
        MENU_ITEMS.BPM_SCHEDULE,
        MENU_ITEMS.BPM_SETTINGS,
      ],
    },
    MENU_ITEMS.TRAINING_SCHEDULE,
    MENU_ITEMS.CALENDAR,
    MENU_ITEMS.FILE_VAULT,
    MENU_ITEMS.HELP_DESK,
    MENU_ITEMS.INVITE_AGENTS,
    MENU_ITEMS.TERMINATED_USERS,
    {
      label: 'Admin',
      icon: '👨‍💼',
      children: [
        MENU_ITEMS.MISSION_RING_PROOF_ADMIN,
        MENU_ITEMS.HELPDESK_ADMIN,
        MENU_ITEMS.FUNCTIONS,
        MENU_ITEMS.FILE_VAULT_ADMIN,
        MENU_ITEMS.TRAINING_CENTER_ADMIN,
        MENU_ITEMS.SYSTEMATIC_TOOLS_ADMIN,
        MENU_ITEMS.HOME_CONTENT_ADMIN,
        MENU_ITEMS.PROMOTION_ADMIN,
        MENU_ITEMS.USER_PERMISSIONS,
        MENU_ITEMS.LEVEL_PERMISSIONS,
      ],
    },
  ] as MenuItem[],
};

/**
 * Get menu structure for a specific plan
 */
const DEFAULT_PLAN: AccountType = Plan.NewAgent;

function normalizePlan(plan: unknown): AccountType {
  if (typeof plan !== 'string') return DEFAULT_PLAN;
  const normalized = Object.values(Plan).find((value) => value === plan);
  return normalized ?? DEFAULT_PLAN;
}

export function getMenuForPlan(plan: unknown): MenuItem[] {
  const normalizedPlan = normalizePlan(plan);
  return PLAN_MENUS[normalizedPlan];
}

/** True if `path` appears anywhere in the (possibly nested) menu. */
export function menuContainsPath(items: MenuItem[], path: string): boolean {
  return items.some(
    (item) => item.path === path || (item.children ? menuContainsPath(item.children, path) : false),
  );
}

/**
 * Label of the menu item for `pathname`: an exact path match wins, otherwise the
 * item whose path is the longest prefix of `pathname` (so `/events/12` finds
 * "Events"). Returns '' when nothing in the menu matches.
 */
export function findMenuLabelByPath(items: MenuItem[], pathname: string): string {
  const best = { label: '', length: -1 };
  const visit = (list: MenuItem[]): string | null => {
    for (const item of list) {
      if (item.path) {
        if (item.path === pathname) return item.label;
        const prefix = item.path.endsWith('/') ? item.path : `${item.path}/`;
        if (item.path !== '/' && pathname.startsWith(prefix) && item.path.length > best.length) {
          best.label = item.label;
          best.length = item.path.length;
        }
      }
      if (item.children) {
        const exact = visit(item.children);
        if (exact) return exact;
      }
    }
    return null;
  };
  return visit(items) ?? best.label;
}

/** True if an item labelled `label` appears anywhere in the (possibly nested) menu. */
function menuContainsLabel(items: MenuItem[], label: string): boolean {
  return items.some(
    (item) =>
      item.label === label || (item.children ? menuContainsLabel(item.children, label) : false),
  );
}

/** Return a copy of the menu with every item matching `path` removed (recurses into children). */
export function removeMenuItemByPath(items: MenuItem[], path: string): MenuItem[] {
  return items
    .filter((item) => item.path !== path)
    .map((item) =>
      item.children ? { ...item, children: removeMenuItemByPath(item.children, path) } : item,
    );
}

/** Paths of the menu entries that carry helpdesk "needs action" badges. */
export const HELPDESK_BADGE_PATHS = {
  supportQueue: MENU_ITEMS.HELPDESK_ADMIN.path as string,
  teamSupport: MENU_ITEMS.TEAM_SUPPORT.path as string,
};

/** Path of the Feature Board entry, which carries the "needs action" badge. */
export const FEATURE_BOARD_BADGE_PATH = MENU_ITEMS.FEATURE_BOARD.path as string;

/**
 * Return a copy of the menu with `badge` set on items whose path is in `counts`
 * (0 clears it). A group's badge is the sum of its children's, so a closed group
 * still shows there is something inside.
 */
export function applyMenuBadges(items: MenuItem[], counts: Record<string, number>): MenuItem[] {
  return items.map((item) => {
    if (item.children) {
      const children = applyMenuBadges(item.children, counts);
      const total = children.reduce((sum, child) => sum + (child.badge ?? 0), 0);
      return { ...item, children, badge: total || undefined };
    }
    const count = item.path ? counts[item.path] : undefined;
    return count ? { ...item, badge: count } : item;
  });
}

/** Return a copy of the menu with every top-level item matching `label` removed. */
export function removeMenuGroupByLabel(items: MenuItem[], label: string): MenuItem[] {
  return items.filter((item) => item.label !== label);
}

/**
 * Within the top-level group `label`, drop every child except the one at `keepPath`.
 * Used to show only the Program entry when no program exists yet, so a manager's
 * clear next action is to create one instead of a list of empty dashboards.
 */
export function keepOnlyGroupChild(
  items: MenuItem[],
  label: string,
  keepPath: string,
): MenuItem[] {
  return items.map((item) =>
    item.label === label && item.children
      ? { ...item, children: item.children.filter((child) => child.path === keepPath) }
      : item,
  );
}

function cloneMenuItems(items: MenuItem[]): MenuItem[] {
  return items.map((item) => ({
    ...item,
    children: item.children ? cloneMenuItems(item.children) : undefined,
  }));
}

export function getMenuForUser(
  plan: unknown,
  hasPromotionAccess: boolean,
  canAccessBuilderAI: boolean = false,
  // Company Owner & Builder are separate things. Defaults to `true` so any
  // caller that doesn't distinguish the two keeps the full owner menu.
  isBuilderAiOwner: boolean = true,
  canAccessMisalignments: boolean = false,
  canAccessProducts: boolean = false,
  canAccessReportingPipeline: boolean = false,
  // wbreporting:manage. Separate from canAccessReportingPipeline, which is the
  // read gate for the operations screen — configuring a contest needs manage.
  canManageReporting: boolean = false,
  // gms:author. A limited rollout like the others: no role holds it, so the entry
  // stays hidden until someone is named in the access console.
  canAuthorGuidance: boolean = false,
  // bpm_settings:manage (Admin and the BPM managers named in BPM Settings). Every
  // plan lists the BPM Settings link; it is removed here for everybody else.
  canManageBpmSettings: boolean = false,
  // plugin_fees:review (the Hierarchy Assistant), reported by plug-in fees my-access.
  canReviewPluginFees: boolean = false,
  // plugin_fees:manage, :review or :payout_approve: the billing cycles screen, and the
  // fee settings screen (the same audience).
  canViewPluginFeeCycles: boolean = false,
  // plugin_fees:manage: recognition and mailing costs, and ledger adjustments.
  canManagePluginFees: boolean = false,
  // plugin_fees:manage or :review: the payments dashboard and follow-ups.
  canViewPluginFeePayments: boolean = false,
  // plugin_fees:manage or :review: the plug-in fees overview and SEVC totals.
  canViewPluginFeeOverview: boolean = false,
  // plugin_fees:manage, :review or :payout_approve: quarterly payouts.
  canViewPluginFeePayouts: boolean = false,
  // The Big Event screens open for at least one event (events my-access `surfaces`).
  bigEventScreens: Partial<Record<BigEventScreen, boolean>> = {},
  // code_of_honor:review or a manage permission (Code of Honor my-access).
  canManageCodeOfHonor: boolean = false,
  // helpdesk:read (Helpdesk my-access `can_view`). Admin plans list Helpdesk Admin
  // statically; it is removed for anyone without the grant and added for any holder.
  canAccessHelpdeskAdmin: boolean = false,
  // Helpdesk my-access `team_queue` (active SMD, or their assistant signed in as them).
  canAccessTeamSupport: boolean = false,
  // feature_requests:create (Feature requests my-access `can_request`).
  canRequestFeatures: boolean = false,
  // feature_requests:manage (`can_manage`): the developer board.
  canManageFeatures: boolean = false,
  // prime_booking:read (Prime booking my-access `can_view`): the booking activity trail.
  canViewPrimeBookingActivity: boolean = false,
  // Any of xcel_prelicensing:view|review|manage (XCEL prelicensing my-access).
  canOpenXcelPrelicensing: boolean = false
): MenuItem[] {
  const normalizedPlan = normalizePlan(plan);
  let menuItems = cloneMenuItems(PLAN_MENUS[normalizedPlan]);

  // Builder AI is gated by backend access (owner / active builder / pending
  // invitee), not by plan — nest it at the end of My Team for anyone allowed.
  if (canAccessBuilderAI && !menuContainsLabel(menuItems, BUILDER_AI_GROUP.label)) {
    const builderAiGroup = cloneMenuItems([BUILDER_AI_GROUP])[0];

    // Company Owner & Builder are separate things: a Company Owner sees the
    // full Builder AI group (unchanged from today), but a Builder is only part
    // of a baseshop and just needs to accept their invitation — so trim their
    // group down to Baseshop + Invitations only.
    if (!isBuilderAiOwner) {
      builderAiGroup.children = builderAiGroup.children?.filter(
        (child) =>
          child.path === MENU_ITEMS.BUILDER_AI_BASESHOP.path ||
          child.path === MENU_ITEMS.BUILDER_AI_INVITATIONS.path
      );
    }

    const myTeam = menuItems.find((item) => item.label === MY_TEAM_LABEL && item.children);
    if (myTeam?.children) {
      myTeam.children.push(builderAiGroup);
    } else {
      // Every plan has My Team today; fall back to just under Home if one stops.
      const homeIdx = menuItems.findIndex((item) => item.label === MENU_ITEMS.HOME.label);
      menuItems.splice(homeIdx >= 0 ? homeIdx + 1 : 0, 0, builderAiGroup);
    }
  }

  // Data Integrity is gated by backend access (misalignments:read), not by
  // plan — inject it only for users the backend authorizes.
  if (
    canAccessMisalignments &&
    !menuItems.some((item) => item.label === DATA_INTEGRITY_GROUP.label)
  ) {
    menuItems.push(cloneMenuItems([DATA_INTEGRITY_GROUP])[0]);
  }

  // Products, Reporting Pipeline and Contest Settings are admin tools on per-user
  // grants, not plan: each goes at the end of the Admin group, in this order, or at top
  // level for a holder whose plan has no Admin group.
  const addAdminEntry = (entry: MenuItem) => {
    if (menuContainsLabel(menuItems, entry.label)) return;
    const copy = cloneMenuItems([entry])[0];
    const adminGroup = menuItems.find((item) => item.label === ADMIN_GROUP_LABEL && item.children);
    if (adminGroup?.children) {
      adminGroup.children.push(copy);
    } else {
      menuItems.push(copy);
    }
  };

  // Product Management: products:read.
  if (canAccessProducts) addAdminEntry(MENU_ITEMS.PRODUCTS);

  // Reporting Pipeline: wbreporting:read or :manage.
  if (canAccessReportingPipeline) addAdminEntry(MENU_ITEMS.REPORTING_PIPELINE);

  // Contest configuration rides wbreporting:manage, the same gate as the pipeline
  // screen and every other reporting configuration model.
  if (canManageReporting) addAdminEntry(MENU_ITEMS.CONTEST_SETTINGS);

  // Code of Honor committee/admin page: any code_of_honor:* access-console grant.
  if (canManageCodeOfHonor) addAdminEntry(MENU_ITEMS.CODE_OF_HONOR);

  // Helpdesk Admin: helpdesk:read. Access-console based, not role based, so an
  // Admin plan without the grant loses the static entry (below) and any grant
  // holder gets it at the end of their Admin group (or at top level).
  if (canAccessHelpdeskAdmin) addAdminEntry(MENU_ITEMS.HELPDESK_ADMIN);

  // Guidance authoring rides gms:author, its own per-user grant.
  if (canAuthorGuidance) addAdminEntry(MENU_ITEMS.GUIDANCE);

  // The plug-in fees admin entries live in one "Plug-in Fees" group, each child on its
  // own grant: overview (manage or review), reviews (review), cycles (manage, review or
  // payout_approve), payments (manage or review), payouts (manage, review or
  // payout_approve), SEVC totals (manage or review), costs and adjustments (manage), fee
  // settings (manage, review or payout_approve — the cycles audience, so the cycles flag).
  const pluginFeesAdminEntries = [
    canViewPluginFeeOverview ? MENU_ITEMS.PLUGIN_FEES_OVERVIEW : null,
    canReviewPluginFees ? MENU_ITEMS.PLUGIN_FEES_REVIEW : null,
    canViewPluginFeeCycles ? MENU_ITEMS.PLUGIN_FEES_CYCLES : null,
    canViewPluginFeePayments ? MENU_ITEMS.PLUGIN_FEES_PAYMENTS : null,
    canViewPluginFeePayouts ? MENU_ITEMS.PLUGIN_FEES_PAYOUTS : null,
    canViewPluginFeeOverview ? MENU_ITEMS.PLUGIN_FEES_SEVC_TOTALS : null,
    canManagePluginFees ? MENU_ITEMS.PLUGIN_FEES_COSTS : null,
    canManagePluginFees ? MENU_ITEMS.PLUGIN_FEES_ADJUSTMENTS : null,
    canViewPluginFeeCycles ? MENU_ITEMS.PLUGIN_FEES_SETTINGS : null,
  ].filter((entry): entry is MenuItem => entry !== null);
  // The group is fee management, so it nests at the end of the Admin group, or goes at
  // top level for a holder whose plan has no Admin group (a Hierarchy Assistant).
  if (pluginFeesAdminEntries.length) {
    addAdminEntry({
      label: PLUGIN_FEES_GROUP_LABEL,
      icon: '💼',
      children: pluginFeesAdminEntries,
    });
  }

  // Big Event sits after BPM (or at the end when a plan has no BPM group), where every
  // plan listed it before it moved off roles and onto access.
  const bigEventEntries = BIG_EVENT_ENTRIES.filter(([screen]) => bigEventScreens[screen]).map(
    ([, entry]) => entry,
  );
  if (bigEventEntries.length && !menuItems.some((item) => item.label === BIG_EVENT_GROUP_LABEL)) {
    const bpmIdx = menuItems.findIndex((item) => item.label === 'BPM');
    const insertAt = bpmIdx >= 0 ? bpmIdx + 1 : menuItems.length;
    menuItems.splice(insertAt, 0, {
      label: BIG_EVENT_GROUP_LABEL,
      icon: '🎪',
      children: cloneMenuItems(bigEventEntries),
    });
  }

  // Team Support (helpdesk L1 queue): last child of My Team, or top level if a plan
  // has no My Team group.
  if (canAccessTeamSupport && !menuContainsLabel(menuItems, MENU_ITEMS.TEAM_SUPPORT.label)) {
    const teamSupport = cloneMenuItems([MENU_ITEMS.TEAM_SUPPORT])[0];
    const myTeam = menuItems.find((item) => item.label === MY_TEAM_LABEL && item.children);
    if (myTeam?.children) myTeam.children.push(teamSupport);
    else menuItems.push(teamSupport);
  }

  // Feature Requests sits right after Help Desk (or at the end if a plan has none).
  if (canRequestFeatures && !menuContainsLabel(menuItems, MENU_ITEMS.FEATURE_REQUESTS.label)) {
    const helpIdx = menuItems.findIndex((item) => item.path === MENU_ITEMS.HELP_DESK.path);
    const entry = cloneMenuItems([MENU_ITEMS.FEATURE_REQUESTS])[0];
    if (helpIdx >= 0) menuItems.splice(helpIdx + 1, 0, entry);
    else menuItems.push(entry);
  }

  // Feature Board: feature_requests:manage, at the end of the Admin group (or top level).
  if (canManageFeatures) addAdminEntry(MENU_ITEMS.FEATURE_BOARD);

  // Prime Booking Activity: prime_booking:read, at the end of the Admin group (or top level).
  if (canViewPrimeBookingActivity) addAdminEntry(MENU_ITEMS.PRIME_BOOKING_ACTIVITY);

  // XCEL Prelicensing: any XCEL capability, at the end of the Admin group (or top level).
  if (canOpenXcelPrelicensing) addAdminEntry(MENU_ITEMS.XCEL_PRELICENSING);

  if (!canAccessHelpdeskAdmin) {
    menuItems = removeMenuItemByPath(menuItems, MENU_ITEMS.HELPDESK_ADMIN.path as string);
  }

  if (!canManageBpmSettings) {
    menuItems = removeMenuItemByPath(menuItems, MENU_ITEMS.BPM_SETTINGS.path as string);
  }

  if (normalizedPlan === Plan.NewAgent && !hasPromotionAccess) {
    menuItems = removeMenuItemByPath(menuItems, MENU_ITEMS.PROMOTION.path as string);
  }

  return menuItems;
}
